import React, { createContext, useContext, useMemo } from 'react';
import { useAuthContext } from 'contexts/AuthContext';
import { useGetOrganizationUsers } from 'hooks/useGetOrganizationUsers';
import { IUser } from 'types/user';

export interface IOrganizationUsersContext {
    /** Map of the selected organization's users keyed by correlation id, for name/avatar lookups. */
    usersByCorrelationId: Map<string, IUser>;
    /** Resolve a single user by correlation id; undefined when unknown, disabled, or still loading. */
    getUser: (correlationId: string | null | undefined) => IUser | undefined;
    /** Correlation id of the authenticated user within the selected organization, if resolvable. */
    currentUserCorrelationId: string | null;
    /** True while the org users are being fetched for the first time (used to avoid flashing fallbacks). */
    isLoading: boolean;
}

const defaultOrganizationUsersContext: IOrganizationUsersContext = {
    usersByCorrelationId: new Map(),
    getUser: () => undefined,
    currentUserCorrelationId: null,
    isLoading: false,
};

const OrganizationUsersContext = createContext<IOrganizationUsersContext>(defaultOrganizationUsersContext);

export const useOrganizationUsersContext = () => useContext(OrganizationUsersContext);

/**
 * Loads the selected organization's users once and exposes them for presentation-only lookups
 * (e.g. UserDisplay), so components can render a name from a correlation id without the backend
 * threading xxxByName fields through every response.
 */
export const OrganizationUsersProvider = ({ children }: { children: React.ReactNode }) => {
    const { selectedOrganizationId, user } = useAuthContext();

    const { data: organizationUsers, isLoading } = useGetOrganizationUsers(
        selectedOrganizationId ?? '',
        { enabled: Boolean(selectedOrganizationId) },
    );

    const usersByCorrelationId = useMemo(
        () => new Map<string, IUser>((organizationUsers ?? []).map(orgUser => [orgUser.correlationId, orgUser])),
        [organizationUsers],
    );

    const currentUserCorrelationId = useMemo(
        () => organizationUsers?.find(orgUser => orgUser.id === user?.id)?.correlationId ?? null,
        [organizationUsers, user],
    );

    const value = useMemo<IOrganizationUsersContext>(
        () => ({
            usersByCorrelationId,
            getUser: correlationId => (correlationId ? usersByCorrelationId.get(correlationId) : undefined),
            currentUserCorrelationId,
            isLoading,
        }),
        [usersByCorrelationId, currentUserCorrelationId, isLoading],
    );

    return (
        <OrganizationUsersContext.Provider value={value}>
            {children}
        </OrganizationUsersContext.Provider>
    );
};

export default OrganizationUsersProvider;
